import React, { useState } from 'react';
import { Button, InputGroup, Input, TemplateModal } from '@nearst/ui';
import * as Sentry from '@sentry/browser';
import { setQuantityBuffer } from '@services/shop';
import { useShop } from '@services/ShopProvider';

import styles from './QuantityBufferModal.module.scss';

const QuantityBufferModal = ({ open, close, shop }) => {
	const { mutate, organisation } = useShop();
	const isOrganisationLevel = !shop;
	const [bufferInput, setBufferInput] = useState(shop?.quantityBuffer || '');
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [successMsg, setSuccessMsg] = useState('');

	const hasExistingBuffer = shop?.quantityBuffer > 0;

	const handleChange = (e) => {
		setErrorMsg('');
		setSuccessMsg('');
		const value = e.target.value;
		if (value === '' || /^\d+$/.test(value)) {
			setBufferInput(value);
		}
	};

	const handleSetBuffer = async () => {
		const value = parseInt(bufferInput, 10);
		if (!Number.isInteger(value) || value < 1) {
			setErrorMsg('Please enter a positive whole number.');
			return;
		}

		setLoading(true);
		setErrorMsg('');
		try {
			// Pass shop.id for shop-level, null/undefined for organisation-level
			const result = await setQuantityBuffer(shop?.id, value);
			await mutate();

			// Customise success message based on context
			const successMessage = isOrganisationLevel
				? `Quantity buffer updated for ${result.shopsUpdated || 'all'} shop${result.shopsUpdated !== 1 ? 's' : ''} in ${organisation?.name || 'the organisation'}. It may take up to 48 hours for all quantities to be updated.`
				: 'Quantity buffer updated. It may take up to 48 hours for all quantities to be updated.';

			setSuccessMsg(successMessage);
		} catch (e) {
			console.error(e);
			Sentry.captureException(e);
			setErrorMsg('Something went wrong. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveBuffer = async () => {
		setLoading(true);
		setErrorMsg('');
		try {
			// Pass shop.id for shop-level, null/undefined for organisation-level
			const result = await setQuantityBuffer(shop?.id, null);
			await mutate();
			setBufferInput('');

			// Customise success message based on context
			const successMessage = isOrganisationLevel
				? `Quantity buffer removed for ${result.shopsUpdated || 'all'} shop${result.shopsUpdated !== 1 ? 's' : ''} in ${organisation?.name || 'the organisation'}. It may take up to 48 hours for all quantities to be updated.`
				: 'Quantity buffer removed. It may take up to 48 hours for all quantities to be updated.';

			setSuccessMsg(successMessage);
		} catch (e) {
			console.error(e);
			Sentry.captureException(e);
			setErrorMsg('Something went wrong. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setErrorMsg('');
		setSuccessMsg('');
		close();
	};

	const title = isOrganisationLevel
		? `Set quantity buffer for ${organisation?.name || 'organisation'}`
		: 'Set quantity buffer';

	return (
		<TemplateModal isOpen={open} onDismiss={handleClose} title={title} showDismissButton={false}>
			<div className={styles.container}>
				<p>
					A quantity buffer reduces the displayed stock quantity for all products by a fixed amount. For example, if a
					product has 10 units in stock and the buffer is set to 3, the quantity shown across your channels will be 7.
				</p>

				{isOrganisationLevel && (
					<p className={styles.info}>
						<strong>Note:</strong> This will apply the same buffer to all shops in your organisation.
					</p>
				)}

				{hasExistingBuffer && (
					<p className={styles.currentBuffer}>
						Current buffer: <strong>{shop.quantityBuffer}</strong>
					</p>
				)}

				<InputGroup label="Buffer amount" htmlFor="quantity-buffer">
					<Input
						id="quantity-buffer"
						type="number"
						min="1"
						step="1"
						disabled={loading}
						value={bufferInput}
						onChange={handleChange}
						placeholder="e.g. 3"
					/>
				</InputGroup>

				{errorMsg && <p className={styles.error}>{errorMsg}</p>}
				{successMsg && <p className={styles.success}>{successMsg}</p>}

				<div className={styles.buttonContainer}>
					<Button disabled={!bufferInput || loading} onClick={handleSetBuffer} primary>
						Set buffer
					</Button>
					{(hasExistingBuffer || isOrganisationLevel) && (
						<Button disabled={loading} onClick={handleRemoveBuffer}>
							Remove buffer
						</Button>
					)}
				</div>
			</div>
		</TemplateModal>
	);
};

export { QuantityBufferModal };